'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface HeaderTransparencyState {
    enabled: boolean;
    textColor: string;
    /** Render the (dark) site logo white while the header floats over the hero. */
    logoWhite?: boolean;
}

const DEFAULT_STATE: HeaderTransparencyState = { enabled: false, textColor: '#ffffff' };

const HeaderTransparencyContext = createContext<{
    state: HeaderTransparencyState;
    setState: (next: HeaderTransparencyState | null) => void;
} | undefined>(undefined);

/**
 * Lets the page's own first block (e.g. a hero with a transparent-header
 * option) tell the globally-rendered <Header> to overlay it transparently —
 * without Header needing to know which page/block requested it. Header and
 * page content are siblings under the store layout, not parent/child, so a
 * shared context is the only way for one to reach the other.
 */
export function HeaderTransparencyProvider({ children, initial }: { children: React.ReactNode; initial?: HeaderTransparencyState | null }) {
    // Seeded from a server-side resolution of the current page's first block (see
    // core/utils/headerTransparency.ts) so Header's fixed/transparent classes and colors
    // are already correct on the very first paint — no sticky-then-fixed flip, and no
    // window where the header is fixed (out of flow) but still rendering its solid
    // colors because the client hasn't hydrated yet. `initial` only seeds this Provider's
    // FIRST mount (a hard navigation); useRegisterHeaderTransparency below still runs
    // client-side to keep this correct across client-side navigations between pages
    // under this same persisted layout, where the server resolution doesn't re-run.
    const [state, setStateRaw] = useState<HeaderTransparencyState>(initial ?? DEFAULT_STATE);
    const setState = useCallback((next: HeaderTransparencyState | null) => {
        const target = next ?? DEFAULT_STATE;
        // Keep the same object for an identical request — a fresh object would re-render every
        // consumer and re-trigger the registering effect below.
        setStateRaw((prev) => (
            prev.enabled === target.enabled && prev.textColor === target.textColor && !!prev.logoWhite === !!target.logoWhite
                ? prev
                : target
        ));
    }, []);
    const value = useMemo(() => ({ state, setState }), [state, setState]);
    return (
        <HeaderTransparencyContext.Provider value={value}>
            {children}
        </HeaderTransparencyContext.Provider>
    );
}

export function useHeaderTransparency(): HeaderTransparencyState {
    const ctx = useContext(HeaderTransparencyContext);
    return ctx?.state ?? DEFAULT_STATE;
}

/** True while a transparent-over-hero header is still at the top of the page. Shared by the
 * builder header shell and its logo/navigation/actions so their color/logo switch is synchronized. */
export function useHeaderAtTransparentTop(threshold = 24): boolean {
    const { enabled } = useHeaderTransparency();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setScrolled(false);
            return;
        }
        const update = () => setScrolled(window.scrollY > threshold);
        update();
        window.addEventListener('scroll', update, { passive: true });
        return () => window.removeEventListener('scroll', update);
    }, [enabled, threshold]);

    return enabled && !scrolled;
}

/**
 * False when the page renders something of its own above the blocks (the page title, the password
 * badge) — the first block is then not at the top of the window, and a header laid over it would sit
 * on that title instead, white-on-white. Mirrors the hide_title check in resolveHeaderTransparency.
 */
const HeaderOverlayAllowedContext = createContext(true);

export function HeaderOverlayGate({ allowed, children }: { allowed: boolean; children: React.ReactNode }) {
    return <HeaderOverlayAllowedContext.Provider value={allowed}>{children}</HeaderOverlayAllowedContext.Provider>;
}

/** Registers this component's desired header transparency while mounted; reverts to solid on unmount/navigation. Pass `null` to opt out. */
export function useRegisterHeaderTransparency(desired: HeaderTransparencyState | null) {
    // Depend on the stable setter, not the context value: the value changes with every state
    // update, which re-ran this effect (cleanup → null, then desired) in an endless loop.
    const setState = useContext(HeaderTransparencyContext)?.setState;
    const overlayAllowed = useContext(HeaderOverlayAllowedContext);
    useEffect(() => {
        if (!setState || !desired || !overlayAllowed) return;
        setState(desired);
        return () => setState(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setState, overlayAllowed, desired?.enabled, desired?.textColor, desired?.logoWhite]);
}
